import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { PrimaryBtn } from "@/components/public/PublicLayout";
import { C, SERIF, EXTERNAL } from "@/lib/publicLinks";

// Mirrors backend assessment_monetization/tiers.py (used only if the API is unreachable).
const FALLBACK = [
  { key: "intro", label: "KISSED Wellness Assessment™ — Intro", price_display: "$47", checkout_url: EXTERNAL.squareTierIntro,
    includes: ["Adaptive Clinical Assessment™", "Core root-cause findings", "Summary recommendations"] },
  { key: "standard", label: "KISSED Wellness Assessment™ — Standard", price_display: "$147", checkout_url: EXTERNAL.squareTierStandard,
    includes: ["Everything in Intro", "Herbal suggestions tuned to your pathway", "Lifestyle recommendations", "Downloadable PDF report"] },
  { key: "premium", label: "KISSED Wellness Assessment™ — Premium", price_display: "$297", checkout_url: EXTERNAL.squareTierPremium,
    includes: ["Everything in Standard", "Expanded protocol recommendations", "Aligned KISSED service recommendations", "Priority clinical review with the KISSED team"] },
];

const clean = (s) => String(s || "").replace(/KCIS\s*/g, "");
const short = (label) => clean(label).split("—").pop().trim();

// Shared tier cards — tier data (price, inclusions, Square link) comes from the
// existing tier registry; `who` copy is owner-editable via Website Manager.
export const AssessmentTiers = ({ who = [], compact = false }) => {
  const [tiers, setTiers] = useState(FALLBACK);
  useEffect(() => {
    api.get("/assessment-purchases/tiers").then(({ data }) => { if (data?.tiers?.length) setTiers(data.tiers); }).catch(() => {});
  }, []);
  const whoFor = (k) => (who.find((w) => w.key === k) || {}).who;
  return (
    <div className="grid lg:grid-cols-3 gap-5 items-stretch" data-testid="assessment-tiers">
      {tiers.map((t) => (
        <div key={t.key} className="rounded-2xl p-6 bg-white flex flex-col" style={{ border: `1px solid ${C.line}` }} data-testid={`tier-card-${t.key}`}>
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>{short(t.label)}</div>
          <div className="mt-2 text-lg text-slate-900" style={SERIF}>{clean(t.label)}</div>
          {!compact && <div className="mt-2 text-4xl font-semibold text-slate-900" data-testid={`tier-price-${t.key}`}>{t.price_display}</div>}
          {whoFor(t.key) && <p className="mt-3 text-sm text-slate-600"><span className="font-semibold text-slate-800">Best for: </span>{whoFor(t.key)}</p>}
          <ul className="mt-4 space-y-2 text-sm flex-1">
            {(t.includes || []).map((i) => (
              <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: C.green }} /><span className="text-slate-700">{clean(i)}</span></li>
            ))}
          </ul>
          {compact ? (
            <a href={`/wellness#choose-depth`} className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-white w-full" style={{ background: C.primary }} data-testid={`tier-view-${t.key}`}>View {short(t.label)} details</a>
          ) : (
            <>
              <PrimaryBtn href={t.checkout_url} className="w-full mt-6" data-testid={`tier-buy-${t.key}`}>Buy {short(t.label)} — {t.price_display}</PrimaryBtn>
              <div className="mt-2 text-[0.7rem] text-center text-slate-400">Secure Square checkout</div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};
